import { OFFICE_NAMES, OfficeType } from 'domain/shared/employee/officeService';
import { ProjectSearchBaseDto } from 'domain/shared/newProject';
import {
  AssignmentReasonDictionaries,
  contractTypesById,
  EmployeeAssignmentContext,
  getAssignmentReasonTitle,
  RelatedEmployeeFilter,
} from '../field/assignmentReasonHelper';
import { PortalComplianceDataType } from '../types';
import { LoadingParams } from 'framework/list';
import { getComplianceRegistryPortalPriority } from 'domain/people/employee/compliance/shared/complianceRegistryPortalRules';
import { PositionType } from 'domain/shared/employee/position/positionService';

export const buildAssignmentReasonDictionaries = (
  offices: OfficeType[],
  positions: PositionType[],
  projects: ProjectSearchBaseDto[],
): AssignmentReasonDictionaries => ({
  officesById: Object.fromEntries(
    (offices || []).map(office => [String(office.id), OFFICE_NAMES[office.name] || office.name]),
  ),
  positionsById: Object.fromEntries((positions || []).map(position => [String(position.id), position.name])),
  contractTypesById,
  accountsById: Object.fromEntries(
    (projects || [])
      .filter(project => !!project.accountData?.accountId && !!project.accountData?.accountName)
      .map(project => [String(project.accountData.accountId), String(project.accountData.accountName)]),
  ),
  projectsById: Object.fromEntries(
    (projects || []).flatMap(project => [
      [String(project.id), project.name],
      [String(project.legacyId), project.name],
    ]),
  ),
  projectIdsByAccountId: (projects || []).reduce(
    (acc, project) => {
      const accountId = project.accountData?.accountId;
      if (!accountId) {
        return acc;
      }

      const key = String(accountId);
      const ids = [String(project.id), String(project.legacyId)].filter(Boolean);
      acc[key] = Array.from(new Set([...(acc[key] || []), ...ids]));

      return acc;
    },
    {} as Record<string, string[]>,
  ),
});

export const buildEmployeeAssignmentContext = (
  employee: any,
  employeeProjectIds: string[],
): EmployeeAssignmentContext => ({
  officeId: employee?.officeId,
  positionId: employee?.positionId,
  contractType: employee?.contractType,
  businessRegistrationCountry: employee?.businessRegistrationCountry,
  residencyRegion: employee?.extendedInformation?.contactInfo?.residencyRegion,
  residencyCountry: employee?.extendedInformation?.contactInfo?.residencyCountry,
  project: employeeProjectIds[0],
  projectId: employeeProjectIds[0],
  projectIds: employeeProjectIds,
});

export const buildGroupedDataByReason = (
  items: PortalComplianceDataType[],
  dictionaries: AssignmentReasonDictionaries,
  employeeContext: EmployeeAssignmentContext,
) => {
  const groupedEntriesMap = new Map<string, PortalComplianceDataType[]>();

  items.forEach(item => {
    const assignmentReasonTitle = getAssignmentReasonTitle(
      item.relatedEmployeeFilter as RelatedEmployeeFilter,
      item.relatedEmployeeProjectIds,
      item.relatedEmployeeAccountIds,
      dictionaries,
      employeeContext,
    );
    const key = assignmentReasonTitle || 'Applies to all employees';

    const group = groupedEntriesMap.get(key) || [];
    group.push(item);
    groupedEntriesMap.set(key, group);
  });

  const getGroupPriority = (groupItems: PortalComplianceDataType[]) =>
    Math.min(2, ...groupItems.map(getComplianceRegistryPortalPriority));

  const sortedEntries = [...groupedEntriesMap.entries()]
    .map(entry => ({
      entry,
      priority: getGroupPriority(entry[1]),
    }))
    .sort((left, right) => left.priority - right.priority || left.entry[0].localeCompare(right.entry[0]))
    .map(({ entry }) => entry);

  return Object.fromEntries(sortedEntries);
};

const getRelatedAccountIds = (items: PortalComplianceDataType[]) =>
  Array.from(new Set((items || []).flatMap(item => item.relatedEmployeeAccountIds || []).map(String)));

export const loadProjectsForPortalReasons = async (
  searchBase: (request: LoadingParams) => Promise<{ result?: ProjectSearchBaseDto[] }>,
  pageItems: PortalComplianceDataType[],
) => {
  const relatedAccountIds = getRelatedAccountIds(pageItems);
  const relatedAccountIdSet = new Set(relatedAccountIds);
  const directProjectIds = Array.from(
    new Set((pageItems || []).flatMap(item => item.relatedEmployeeProjectIds || []).map(value => String(value))),
  );
  const legacyIds = Array.from(new Set(directProjectIds.filter(Boolean)));

  const safeSearchBase = async (request: LoadingParams): Promise<ProjectSearchBaseDto[]> => {
    try {
      const response = await searchBase(request);
      return response.result || [];
    } catch {
      return [];
    }
  };

  const loadProjectsByAccountIds = async () => {
    if (!relatedAccountIds.length) {
      return [];
    }

    return safeSearchBase({
      pageNumber: 0,
      pageSize: 200,
      filter: {
        accountId: { in: relatedAccountIds },
      },
    });
  };

  const [byLegacy, byAccounts] = await Promise.all([
    legacyIds.length
      ? safeSearchBase({
          pageNumber: 0,
          pageSize: 200,
          filter: {
            legacyId: { in: legacyIds },
          },
        })
      : Promise.resolve([]),
    loadProjectsByAccountIds(),
  ]);

  const projectsById = new Map<string, ProjectSearchBaseDto>();

  const accountScopedProjects = byAccounts.filter(project =>
    relatedAccountIdSet.has(String(project.accountData?.accountId || '')),
  );

  [...byLegacy, ...accountScopedProjects].forEach(project => {
    projectsById.set(String(project.id), project);
    projectsById.set(String(project.legacyId), project);
  });

  return Array.from(new Set(projectsById.values()));
};
